/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React from 'react';
import { List, WindowScrollerChildProps } from 'react-virtualized';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import { LDProps } from '../../../../../../skeleton/src/core/providers/launch-darkly';
import { CELL_WIDTH } from '../../../../core/constants';
import { PredictionsAccountCategories, VIEW_TYPES } from '../../../../core/types';
import { DataGridLayoutContext } from '../../../../ui/data-grid/components/data-grid-layout-context';
import { UserGridSettingsContextType } from '../user-grid-settings/types';
import { getRowHeightSize } from '../user-grid-settings/user-grid-settings-actions';
import { withUserGridSettingsContext } from '../user-grid-settings/user-grid-settings-context';
import { ROW_KIND } from './constants';
import { RowAccount } from './rows/components/row-account';
import { RowAssumption } from './rows/components/row-assumption';
import { getRowHeight, getWrapperStyle } from './style-rows';
import { HeightProps, RenderRowProps, RowsType, RowType } from './types';

import grid from '../../../../ui/data-grid/components/grid.scss';

interface OwnProps {
  rows: RowsType;
  windowScrollerChildProps: WindowScrollerChildProps;
  showZeroAndHighlightForChart?: boolean;
  columns: string[];
  category?: PredictionsAccountCategories;
  viewType?: VIEW_TYPES;
  isBudgetCreateOnboarding?: boolean;
}

type UserGridSettingsContextProps = Pick<UserGridSettingsContextType, 'rowHeights' | 'showDecimals'>;

type Props = UserGridSettingsContextProps & OwnProps & LDProps;

export class VirtualizedRowsComponent extends React.PureComponent<Props> {
  // TODO: Replace contextType with useDataGridLayout when this is refactored into a function component.
  public static contextType = DataGridLayoutContext;

  public context!: React.ContextType<typeof DataGridLayoutContext>;

  private isVirtualizationDisabled = (): boolean => {
    const params = new URLSearchParams(window.location.search);
    return params.has('disableVirtualisation');
  };

  public render(): JSX.Element {
    const { rowHeights, showDecimals, rows, columns, windowScrollerChildProps } = this.props;
    const rowHeight = getRowHeightSize(rowHeights);
    const { height, isScrolling, onChildScroll, scrollTop } = windowScrollerChildProps;
    const listWidth = this.context?.contentWidth || columns.length * CELL_WIDTH;

    const disableVirtualization = this.isVirtualizationDisabled();

    if (disableVirtualization) {
      return (
        <div className={grid.virtualizedList} style={{ width: listWidth }}>
          {rows.map((row, index) => {
            const rowHeightValue = this.getRowHeight({ index });
            const style = { ...{ height: rowHeightValue } };
            return this.rowRenderer({
              index,
              key: `row-${index}`,
              style,
              isScrolling: false,
              isVisible: true
            });
          })}
        </div>
      );
    }

    return (
      <List
        key={`${rowHeight}-${rows.length}`}
        autoHeight={true}
        itemData={rows}
        isScrolling={isScrolling}
        onScroll={onChildScroll}
        scrollTop={scrollTop}
        className={grid.virtualizedList}
        height={height}
        rowCount={rows.length}
        rowHeight={this.getRowHeight}
        showDecimals={showDecimals}
        width={listWidth}
        rowRenderer={this.rowRenderer}
      />
    );
  }

  private rowRenderer = ({ index, key, style }: RenderRowProps) => {
    const { rows, rowHeights, showDecimals, columns, category, viewType, isBudgetCreateOnboarding } = this.props;
    const rowHeight = getRowHeightSize(rowHeights);
    const row = rows[index];

    if (isBudgetCreateOnboarding) {
      row.account = {
        ...row.account,
        period_data: row.account.period_data.slice(0, 13)
      };
    }

    if (row.kind === ROW_KIND.heading) {
      return null;
    }

    if (
      row.kind === ROW_KIND.assumption ||
      row.kind === ROW_KIND.paymentType ||
      row.kind === ROW_KIND.sheetsAssumption ||
      row.kind === ROW_KIND.systemCalc
    ) {
      return (
        <div style={{ ...this.getWrapperStyle(index, style, row) }} key={key} role="row">
          <RowAssumption
            row={row}
            rowHeight={rowHeight}
            showDecimals={showDecimals}
            isLast={row.siblingIndex === row.siblingCount - 1}
            columns={columns}
            category={category}
            viewType={viewType}
          />
        </div>
      );
    }

    const { showZeroAndHighlightForChart } = this.props;

    return (
      <div style={{ ...this.getWrapperStyle(index, style, row) }} key={key} role="row">
        <RowAccount
          row={row}
          rowHeight={rowHeight}
          showDecimals={showDecimals}
          level={row.level}
          showZeroAndHighlightForChart={showZeroAndHighlightForChart}
          columns={columns}
          category={category}
        />
      </div>
    );
  };

  private getWrapperStyle = (index: number, style: React.CSSProperties, row: RowType) => {
    const { rowHeights } = this.props;
    const rowHeight = getRowHeightSize(rowHeights);

    return getWrapperStyle(index, style, rowHeight, row, true);
  };

  private getRowHeight = ({ index }: HeightProps) => {
    const { rowHeights, rows } = this.props;

    return getRowHeight(index, rowHeights, rows[index], true);
  };
}

export const RowsVirtualized = withLDConsumer()(
  withUserGridSettingsContext<OwnProps, UserGridSettingsContextProps>(VirtualizedRowsComponent, [
    'rowHeights',
    'showDecimals'
  ])
);
